'use client';

import React from 'react';
import { ComponentNode } from '../types/template';
import {
    convertStylesToInline,
    getColumnClasses,
    getCustomThemeColumnFlexStyle,
    getCustomThemeGridStyle,
    getCustomThemeItemsLayoutStyle,
    isResponsiveSidebar,
    isResponsiveSplitLayout,
} from '../utils/styleUtils';
import { resolveCustomThemeHref } from '../utils/linkHelpers';
import TextComponent from './TextComponent';
import HeadingComponent from './HeadingComponent';
import ButtonComponent from './ButtonComponent';
import ImageComponent from './ImageComponent';
import LinkComponent from './LinkComponent';
import IconComponent from './IconComponent';
import SearchFormComponent from './SearchFormComponent';
import NewsletterFormComponent from './NewsletterFormComponent';
import FaqItemComponent from './FaqItemComponent';
import ProductDetailTrigger from './ProductDetailTrigger';
import { getNestedValue, hasRenderableValue } from '../utils/dataBinding';
import { normalizeTemplateItem } from '../utils/normalizeTemplateItem';

interface ClientComponentRendererProps {
    component: ComponentNode;
    data?: any;
    context?: {
        companyId?: string;
        templateId?: string;
        domain?: string;
        pageSlug?: string;
        params?: Record<string, any>;
        searchParams?: Record<string, any>;
        store_slug?: string;
        slug_type?: string;
        category_slug?: string;
        promotion_slug?: string;
        out_link?: string;
    };
}

const structuralSectionTypes = new Set([
    'HeroBanner',
    'CategorySection',
    'FaqSection',
    'FAQ_ITEM',
    'MerchantSection',
    'OffersSection',
    'OfferSection',
    'EventOfferSection',
    'EventSection',
    'MerchantsAlphabetSection',
    'ProductsSection',
    'TopDealsSection',
    'PromotionSection',
    'StoreSearchSection',
    'NewsletterSection',
    'BreadcrumbSection',
    'Pagination',
    'TagsSection',
    'Footer',
    'BlogSection',
    'CustomBanner',
    'PopularDeals',
    'FooterCategories',
    'CombinedOffersSection',
    'InnerCategoriesSection',
    'SECTION',
]);

const renderChildren = (
    components: ComponentNode[] | undefined,
    data: any,
    context: ClientComponentRendererProps['context']
) =>
    components?.map((child) => (
        <ClientComponentRenderer
            key={child.id}
            component={child}
            data={data}
            context={context}
        />
    ));

const ClientComponentRenderer: React.FC<ClientComponentRendererProps> = ({
    component,
    data,
    context,
}) => {
    const { type, styles, customClassName, components, href, size, repeated, dataSourceKey, gridCols, gridGap, itemsLayout } = component;
    const inlineStyles = convertStylesToInline(styles);

    // An author chose to hide this part outright (unconditional, unlike
    // hideIfEmpty's data-driven check) — e.g. turning off a card's badge.
    if (component.hidden) {
        return null;
    }

    // Skip entirely rather than show a blank shell — the WhatsApp button and
    // the footer's Company-column blog link both only exist for companies
    // that actually have that field set on get_company_data.
    if (
        component.hideIfEmpty &&
        !hasRenderableValue(getNestedValue(data, component.hideIfEmpty))
    ) {
        return null;
    }
    const rowStyles =
        type === 'ROW'
            ? {
                ...inlineStyles,
                '--custom-theme-column-gap':
                    typeof styles?.gap === 'number' ? `${styles.gap}px` : styles?.gap ?? '0px',
            }
            : inlineStyles;

    if (component.openDetailsModal && (type === 'P' || type === 'TEXT' || type === 'BUTTON' || type === 'LINK')) {
        return <ProductDetailTrigger component={component} data={data} context={context} />;
    }

    switch (type) {
        case 'H1':
        case 'H2':
        case 'H3':
        case 'H4':
            return <HeadingComponent component={component} data={data} />;

        case 'P':
        case 'TEXT':
            return <TextComponent component={component} data={data} context={context} />;

        case 'BUTTON':
            return <ButtonComponent component={component} data={data} context={context} />;

        case 'LINK':
            return <LinkComponent component={component} data={data} context={context} />;

        case 'ICON':
            return <IconComponent component={component} data={data} context={context} />;

        case 'IMAGE':
            return <ImageComponent component={component} data={data} context={context} />;

        case 'FAQ_ITEM':
            return <FaqItemComponent component={component} data={data} />;

        case 'SEARCH_FORM':
            return <SearchFormComponent component={component} context={context} />;

        case 'NEWSLETTER_FORM':
            return <NewsletterFormComponent component={component} context={context} />;

        case 'FooterCategories': {
            if (component.dataSource === 'auto') {
                const categories = Array.isArray(data?.categories) ? data.categories : [];
                const cardTemplate = components?.[0];

                if (!cardTemplate || categories.length === 0) {
                    return null;
                }

                return (
                    <div style={inlineStyles} className={customClassName}>
                        {categories.map((category: any, index: number) => (
                            <ClientComponentRenderer
                                key={`${component.id}-${category.id ?? index}`}
                                component={cardTemplate}
                                data={category}
                                context={context}
                            />
                        ))}
                    </div>
                );
            }

            return (
                <section style={inlineStyles} className={customClassName}>
                    {renderChildren(components, data, context)}
                </section>
            );
        }

        case 'CARD': {
            const repeatedSource = (() => {
                if (Array.isArray(data)) {
                    return data;
                }

                if (repeated && dataSourceKey) {
                    const nestedSource = getNestedValue(data, dataSourceKey);
                    if (Array.isArray(nestedSource)) {
                        return nestedSource;
                    }
                }

                return null;
            })();

            if (repeatedSource) {
                const items = component.totalCards ? repeatedSource.slice(0, component.totalCards) : repeatedSource;
                const cardGridStyle = getCustomThemeItemsLayoutStyle(itemsLayout, gridCols, gridGap);

                return (
                    <div style={cardGridStyle} className={itemsLayout === 'flex-wrap' || itemsLayout === 'flex-nowrap' ? undefined : 'cb-responsive-grid'}>
                        {items.map((item: any, index: number) => (
                            <div key={index} style={inlineStyles}>
                                {renderChildren(components, normalizeTemplateItem(item), context)}
                            </div>
                        ))}
                    </div>
                );
            }

            return (
                <div style={inlineStyles}>
                    {renderChildren(components, data, context)}
                </div>
            );
        }

        case 'CONTAINER': {
            const containerClassName = [
                customClassName,
                isResponsiveSplitLayout(component) ? 'cb-responsive-split-layout' : undefined,
                isResponsiveSidebar(component) ? 'cb-responsive-sidebar' : undefined,
            ].filter(Boolean).join(' ') || undefined;
            const offset = component.offset;
            const applyOffset = <T,>(items: T[]): T[] => {
                if (!offset) return items;
                if (offset === 'half') return items.slice(0, Math.ceil(items.length / 2));
                if (offset === 'secondHalf') return items.slice(Math.ceil(items.length / 2));
                return items.slice(offset);
            };
        
            const applyTotalCards = <T,>(items: T[]): T[] =>
                component.totalCards ? items.slice(0, component.totalCards) : items;
            const repeatedSource = (() => {
                if (Array.isArray(data)) {
                    return applyTotalCards(applyOffset(data));
                }

                if (repeated && dataSourceKey) {
                    const nestedSource = getNestedValue(data, dataSourceKey);
                    if (Array.isArray(nestedSource)) {
                        return applyTotalCards(applyOffset(nestedSource));
                    }
                }

                return null;
            })();

            if (repeatedSource) {
                return (
                    <>
                        {repeatedSource.map((item: any, index: number) => {
                            const normalizedItem = normalizeTemplateItem(item);
                            let repeatedHref = href;
                            if (repeatedHref) {
                                repeatedHref = resolveCustomThemeHref(repeatedHref, normalizedItem, context) || repeatedHref;
                            }

                            const repeatedContent = renderChildren(components, normalizedItem, context);

                            if (repeatedHref && repeatedHref !== '#' && !repeatedHref.startsWith('[')) {
                                return (
                                    <a
                                        key={`${component.id}-${index}`}
                                        href={repeatedHref}
                                        target={(component as any).target}
                                        rel={(component as any).rel}
                                        style={inlineStyles}
                                        className={containerClassName}
                                    >
                                        {repeatedContent}
                                    </a>
                                );
                            }

                            return (
                                <div
                                    key={`${component.id}-${index}`}
                                    style={inlineStyles}
                                    className={containerClassName}
                                >
                                    {repeatedContent}
                                </div>
                            );
                        })}
                    </>
                );
            }

            let resolvedHref = href;
            if (resolvedHref) {
                resolvedHref = resolveCustomThemeHref(resolvedHref, data, context) || resolvedHref;
            }

            const content = renderChildren(components, data, context);
            if (resolvedHref && resolvedHref !== '#' && !resolvedHref.startsWith('[')) {
                return (
                    <a
                        href={resolvedHref}
                        target={(component as any).target}
                        rel={(component as any).rel}
                        style={inlineStyles}
                        className={containerClassName}
                    >
                        {content}
                    </a>
                );
            }

            return (
                <div style={inlineStyles} className={containerClassName}>
                    {content}
                </div>
            );
        }

        case 'COLUMN': {
            const colClasses = getColumnClasses(size);
            const responsiveStyles: React.CSSProperties = {
                ...inlineStyles,
                ...getCustomThemeColumnFlexStyle(size),
            };

            return (
                <div
                    style={responsiveStyles}
                    className={['cb-responsive-column', colClasses, customClassName].filter(Boolean).join(' ')}
                >
                    {renderChildren(components, data, context)}
                </div>
            );
        }

        case 'ROW':
        case 'GRID':
            return (
                <div style={type === 'GRID' ? { ...inlineStyles, ...getCustomThemeGridStyle(gridCols, gridGap ?? (typeof styles?.gap === 'number' ? styles.gap : undefined)) } : rowStyles} className={['cb-responsive-row', type === 'GRID' ? 'cb-responsive-grid' : '', customClassName].filter(Boolean).join(' ')}>
                    {renderChildren(components, data, context)}
                </div>
            );

        default:
            // "SECTION" is deliberately NOT its own case above — it must fall
            // through to this structuralSectionTypes check so a generic block
            // (Offers Banner, Sidebar) with a repeated CARD renders through
            // the same <section> path every named section type uses, instead
            // of the bare unexpanded <div> the ROW/GRID case gives it.
            if (structuralSectionTypes.has(type)) {
                return (
                    <section
                        style={inlineStyles}
                        className={[
                            customClassName,
                            type === 'NewsletterSection' ? 'cb-newsletter-section' : undefined,
                            isResponsiveSidebar(component) ? 'cb-responsive-sidebar' : undefined,
                        ].filter(Boolean).join(' ')}
                    >
                        {renderChildren(components, data, context)}
                    </section>
                );
            }

            console.warn(`Unknown client component type: ${type}`);
            return null;
    }
};

export default ClientComponentRenderer;
